import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TreatmentPlanSession,
  TreatmentPlanSessionListItem,
  WizardStep,
  DocumentType,
  DOCUMENT_TYPES,
  DocumentAnalysisResponse,
  InputPanelState,
  SectionGenerationState,
  ModelInfo,
  UploadedFileInfo,
} from "../types";
import {
  getSession,
  updateSessionStep,
  listSessions,
  createSession,
  deleteSession,
  clearSessionDocument,
} from "../services/treatmentPlanSessions";
import {
  analyzeDocument,
  analyzeVBMAPP,
  getExcelPreview,
  generateTreatmentPlanSection,
  getSectionConfig,
} from "../services/api";
import { GenerationSection } from "./GenerateDraftSidebar";
import ClientSelector from "./ClientSelector";
import TreatmentPlanSidebar from "./TreatmentPlanSidebar";
import WizardStepIndicator from "./WizardStepIndicator";
import UploadDocumentsStep from "./UploadDocumentsStep";
import SectionGenerationStep from "./SectionGenerationStep";
import ReviewStep, { ReviewStepHandle } from "./ReviewStep";
import "./TreatmentPlanWizard.css";

interface TreatmentPlanWizardProps {
  models: ModelInfo[];
}

const createInitialInputState = (): InputPanelState => ({
  file: null,
  selectedModel: "",
  systemPrompt: "",
  userPrompt: "",
  outputSchema: {},
  showPreview: false,
  showAdvancedOptions: false,
});

const createInitialSectionState = (): SectionGenerationState => ({
  selectedModel: "",
  systemPrompt: "",
  userPrompt: "",
  selectedReferences: [],
  generatedContent: null,
  isGenerating: false,
  lastGeneratedAt: null,
  lastGeneratedModel: null,
  hasUnsavedChanges: false,
  ragEnabled: true,
  ragNumChunks: 5,
  ragMinScore: 0.3,
});

const TreatmentPlanWizard: React.FC<TreatmentPlanWizardProps> = ({
  models,
}) => {
  // Session management state
  const [sessions, setSessions] = useState<TreatmentPlanSessionListItem[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [activeSession, setActiveSession] = useState<TreatmentPlanSession | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.UPLOAD_DOCUMENTS);

  // Upload Documents state (Step 1)
  const [activeDocumentType, setActiveDocumentType] = useState<DocumentType>(DocumentType.VB_MAPP);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFileInfo>>({});
  const [analysisResultsMap, setAnalysisResultsMap] = useState<Record<DocumentType, DocumentAnalysisResponse | null>>({
    [DocumentType.VB_MAPP]: null,
    [DocumentType.PARENT_INTERVIEW]: null,
    [DocumentType.VINELAND_3]: null,
    [DocumentType.DIAGNOSTIC_REPORT]: null,
    [DocumentType.IEP]: null,
    [DocumentType.INTAKE_FORM]: null,
    [DocumentType.PREVIOUS_ABA_PLAN]: null,
    [DocumentType.OBSERVATION_NOTE]: null,
    [DocumentType.QABF]: null,
  });
  const [inputStateMap, setInputStateMap] = useState<Record<DocumentType, InputPanelState>>({
    [DocumentType.VB_MAPP]: createInitialInputState(),
    [DocumentType.PARENT_INTERVIEW]: createInitialInputState(),
    [DocumentType.VINELAND_3]: createInitialInputState(),
    [DocumentType.DIAGNOSTIC_REPORT]: createInitialInputState(),
    [DocumentType.IEP]: createInitialInputState(),
    [DocumentType.INTAKE_FORM]: createInitialInputState(),
    [DocumentType.PREVIOUS_ABA_PLAN]: createInitialInputState(),
    [DocumentType.OBSERVATION_NOTE]: createInitialInputState(),
    [DocumentType.QABF]: createInitialInputState(),
  });
  // Track which files have been analyzed (to detect changes)
  const [analyzedFileKeys, setAnalyzedFileKeys] = useState<Record<string, string>>({});
  // Doc types cleared locally but not yet synced to the server (synced on "Next")
  const [pendingClears, setPendingClears] = useState<Set<DocumentType>>(new Set());

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  type DocAnalysisStatus = "pending" | "analyzing" | "done" | "error";
  const [analysisProgress, setAnalysisProgress] = useState<
    { total: number; docs: { type: DocumentType; name: string; status: DocAnalysisStatus }[] } | null
  >(null);

  // Section Generation state (Steps 2-5)
  const [activeSection, setActiveSection] = useState<GenerationSection>(GenerationSection.PATIENT_INFORMATION);
  const [sectionStates, setSectionStates] = useState<Map<GenerationSection, SectionGenerationState>>(new Map());
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  type SectionGenStatus = "pending" | "generating" | "done" | "skipped" | "error";
  const [generationProgress, setGenerationProgress] = useState<
    { total: number; sections: { id: GenerationSection; name: string; status: SectionGenStatus }[] } | null
  >(null);

  // UI state

  const reviewStepRef = useRef<ReviewStepHandle>(null);
  // Derived state
  const generatedSections = new Map<GenerationSection, any>();
  sectionStates.forEach((state, section) => {
    if (state.generatedContent) {
      generatedSections.set(section, state.generatedContent);
    }
  });

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Load active session when ID changes
  useEffect(() => {
    let cancelled = false;

    if (activeSessionId) {
      loadSession(activeSessionId, () => cancelled);
    } else {
      setActiveSession(null);
      setCurrentStep(WizardStep.UPLOAD_DOCUMENTS);
      resetLocalState();
    }

    return () => {
      cancelled = true;
    };
  }, [activeSessionId]);

  const loadSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const sessionList = await listSessions();
      setSessions(sessionList);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId: number, isCancelled?: () => boolean) => {
    setIsLoadingSession(true);
    try {
      const session = await getSession(sessionId);
      if (isCancelled?.()) return;
      setActiveSession(session);
      setCurrentStep(session.current_step as WizardStep);
      hydrateStateFromSession(session);
    } catch (error) {
      if (isCancelled?.()) return;
      console.error("Failed to load session:", error);
      setActiveSessionId(null);
    } finally {
      if (!isCancelled?.()) {
        setIsLoadingSession(false);
      }
    }
  };

  const hydrateStateFromSession = (session: TreatmentPlanSession) => {
    if (session.analyzed_documents) {
      setAnalysisResultsMap(prev => ({
        ...prev,
        ...session.analyzed_documents,
      }));
    }

    if (session.section_states) {
      const newSectionStates = new Map<GenerationSection, SectionGenerationState>();
      Object.entries(session.section_states).forEach(([key, value]) => {
        newSectionStates.set(key as GenerationSection, value as SectionGenerationState);
      });
      setSectionStates(newSectionStates);
    }

    if (session.generated_sections) {
      setSectionStates(prev => {
        const merged = new Map(prev);
        Object.entries(session.generated_sections).forEach(([key, value]) => {
          const existing = merged.get(key as GenerationSection) || createInitialSectionState();
          merged.set(key as GenerationSection, {
            ...existing,
            generatedContent: value,
          });
        });
        return merged;
      });
    }

    if (session.input_states) {
      // Strip `file` — File objects cannot survive JSON serialization; any
      // residual truthy value would break the `isAnalysisCurrent` check in
      // the sidebar (it enters the localFile branch and fails the name
      // comparison).  Server-persisted files live in `uploaded_files`.
      const sanitized: Record<string, InputPanelState> = {};
      Object.entries(session.input_states).forEach(([key, value]) => {
        sanitized[key] = { ...(value as InputPanelState), file: null };
      });
      setInputStateMap(prev => ({
        ...prev,
        ...sanitized,
      }));
    }

    if (session.uploaded_files) {
      setUploadedFiles(session.uploaded_files);
      const keys: Record<string, string> = {};
      Object.entries(session.uploaded_files).forEach(([docType, fileInfo]) => {
        if (session.analyzed_documents?.[docType]) {
          keys[docType] = (fileInfo as UploadedFileInfo).filename || "";
        }
      });
      setAnalyzedFileKeys(keys);
    }
  };

  const resetLocalState = () => {
    setAnalysisResultsMap({
      [DocumentType.VB_MAPP]: null,
      [DocumentType.PARENT_INTERVIEW]: null,
      [DocumentType.VINELAND_3]: null,
      [DocumentType.DIAGNOSTIC_REPORT]: null,
      [DocumentType.IEP]: null,
      [DocumentType.INTAKE_FORM]: null,
      [DocumentType.PREVIOUS_ABA_PLAN]: null,
      [DocumentType.OBSERVATION_NOTE]: null,
      [DocumentType.QABF]: null,
    });
    setInputStateMap({
      [DocumentType.VB_MAPP]: createInitialInputState(),
      [DocumentType.PARENT_INTERVIEW]: createInitialInputState(),
      [DocumentType.VINELAND_3]: createInitialInputState(),
      [DocumentType.DIAGNOSTIC_REPORT]: createInitialInputState(),
      [DocumentType.IEP]: createInitialInputState(),
      [DocumentType.INTAKE_FORM]: createInitialInputState(),
      [DocumentType.PREVIOUS_ABA_PLAN]: createInitialInputState(),
      [DocumentType.OBSERVATION_NOTE]: createInitialInputState(),
      [DocumentType.QABF]: createInitialInputState(),
    });
    setUploadedFiles({});
    setSectionStates(new Map());
    setActiveDocumentType(DocumentType.VB_MAPP);
    setActiveSection(GenerationSection.PATIENT_INFORMATION);
    setAnalyzedFileKeys({});
    setPendingClears(new Set());
    setIsAnalyzing(false);
    setAnalysisProgress(null);
  };

  const handleCreateSession = async (clientName: string, dateOfBirth: string | null) => {
    try {
      const session = await createSession({
        client_name: clientName,
        date_of_birth: dateOfBirth,
      });
      await loadSessions();
      setActiveSessionId(session.id);
    } catch (error) {
      console.error("Failed to create session:", error);
      throw error;
    }
  };

  const handleResumeSession = (sessionId: number) => {
    setActiveSessionId(sessionId);
  };

  const handleDeleteSession = async (sessionId: number) => {
    try {
      await deleteSession(sessionId);
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
      await loadSessions();
    } catch (error) {
      console.error("Failed to delete session:", error);
    }
  };

  const handleStepChange = async (step: WizardStep) => {
    if (!activeSessionId) return;

    try {
      await updateSessionStep(activeSessionId, step);
      setCurrentStep(step);
    } catch (error) {
      console.error("Failed to update step:", error);
    }
  };




  // Determine which documents need analysis (new or changed files)
  const getDocumentsToAnalyze = useCallback((): DocumentType[] => {
    const toAnalyze: DocumentType[] = [];

    for (const dt of DOCUMENT_TYPES) {
      const file = inputStateMap[dt.type]?.file;
      const existingUpload = uploadedFiles[dt.type];

      if (!file && !existingUpload) continue;

      // New local file that hasn't been analyzed with this name
      if (file) {
        const prevKey = analyzedFileKeys[dt.type];
        if (prevKey !== file.name) {
          toAnalyze.push(dt.type);
        }
      }
    }

    return toAnalyze;
  }, [inputStateMap, uploadedFiles, analyzedFileKeys]);

  /** Merge server session snapshot so uploaded_files / analyzed_documents match DB after analyze */
  const refreshSessionFromServer = async () => {
    if (!activeSessionId) return;
    try {
      const refreshed = await getSession(activeSessionId);
      setActiveSession(refreshed);
      if (refreshed.uploaded_files) {
        setUploadedFiles(refreshed.uploaded_files);
      }
      if (refreshed.analyzed_documents) {
        setAnalysisResultsMap(prev => ({
          ...prev,
          ...refreshed.analyzed_documents,
        }));
      }
      const keys: Record<string, string> = {};
      Object.entries(refreshed.uploaded_files || {}).forEach(([dt, info]) => {
        if (refreshed.analyzed_documents?.[dt]) {
          keys[dt] = (info as UploadedFileInfo).filename || "";
        }
      });
      setAnalyzedFileKeys(prev => ({ ...prev, ...keys }));
    } catch (e) {
      console.error("Failed to refresh session from server:", e);
    }
  };

  // Analyze all pending documents in parallel
  const analyzeAllDocuments = async (): Promise<boolean> => {
    const docsToAnalyze = getDocumentsToAnalyze();

    if (docsToAnalyze.length === 0) {
      await refreshSessionFromServer();
      return true;
    }

    const initialDocs = docsToAnalyze.map(docType => ({
      type: docType,
      name: DOCUMENT_TYPES.find(dt => dt.type === docType)?.name || docType,
      status: "pending" as DocAnalysisStatus,
    }));

    setIsAnalyzing(true);
    setAnalysisProgress({ total: docsToAnalyze.length, docs: initialDocs });

    const updateDocStatus = (docType: DocumentType, status: DocAnalysisStatus) => {
      setAnalysisProgress(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          docs: prev.docs.map(d => d.type === docType ? { ...d, status } : d),
        };
      });
    };

    const analyzeSingleDoc = async (docType: DocumentType): Promise<boolean> => {
      updateDocStatus(docType, "analyzing");
      try {
        const file = inputStateMap[docType]?.file;
        let result: DocumentAnalysisResponse;

        if (docType === DocumentType.VB_MAPP) {
          result = await analyzeVBMAPP(file ?? null, activeSessionId);
        } else if (
          docType === DocumentType.QABF &&
          file &&
          (file.name.toLowerCase().endsWith(".xlsx") || file.name.toLowerCase().endsWith(".xls"))
        ) {
          const preview = await getExcelPreview(file);
          if (preview.error || !preview.pdf_data) {
            throw new Error(preview.error || "Could not convert QABF Excel to PDF for analysis");
          }
          const binaryString = atob(preview.pdf_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          const pdfFile = new File(
            [bytes],
            file.name.replace(/\.(xlsx|xls)$/i, ".pdf"),
            { type: "application/pdf" }
          );
          result = await analyzeDocument(docType, pdfFile, activeSessionId);
        } else {
          result = await analyzeDocument(docType, file ?? null, activeSessionId);
        }

        setAnalysisResultsMap(prev => ({ ...prev, [docType]: result }));
        if (file) {
          setAnalyzedFileKeys(prev => ({ ...prev, [docType]: file.name }));
        }
        updateDocStatus(docType, "done");
        return true;
      } catch (error) {
        console.error(`Failed to analyze ${docType}:`, error);
        updateDocStatus(docType, "error");
        return false;
      }
    };

    try {
      const results = await Promise.allSettled(
        docsToAnalyze.map(docType => analyzeSingleDoc(docType))
      );
      await refreshSessionFromServer();
      return results.every(r => r.status === "fulfilled" && r.value === true);
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(null);
    }
  };

  /** Flush pending clears to the server and update local baseline state. */
  const syncPendingClears = async () => {
    if (!activeSessionId || pendingClears.size === 0) return;
    const clearedTypes = Array.from(pendingClears);
    const promises = clearedTypes.map(docType =>
      clearSessionDocument(activeSessionId, docType).catch(err =>
        console.error(`Failed to clear ${docType} on server:`, err)
      )
    );
    await Promise.all(promises);

    // Update local baseline to match server
    setUploadedFiles(prev => {
      const next = { ...prev };
      clearedTypes.forEach(dt => delete next[dt]);
      return next;
    });
    setAnalysisResultsMap(prev => {
      const next = { ...prev };
      clearedTypes.forEach(dt => delete next[dt]);
      return next;
    });
    setAnalyzedFileKeys(prev => {
      const next = { ...prev };
      clearedTypes.forEach(dt => delete next[dt]);
      return next;
    });
    setPendingClears(new Set());
  };

  const handleApplyChanges = async () => {
    await syncPendingClears();
    await analyzeAllDocuments();
  };

  const buildReferenceData = (
    selectedRefs: string[],
    currentAnalysis: Record<DocumentType, DocumentAnalysisResponse | null>,
    currentGenerated: Map<GenerationSection, any>,
  ): Array<{ title: string; content: any }> => {
    const result: Array<{ title: string; content: any }> = [];
    for (const refId of selectedRefs) {
      if (refId.startsWith("analyzed-")) {
        const docType = refId.replace("analyzed-", "");
        const analysis = currentAnalysis[docType as DocumentType];
        if (analysis?.data) {
          const label = docType.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
          result.push({ title: label, content: analysis.data });
        }
      } else if (refId.startsWith("section-")) {
        const sectionId = refId.replace("section-", "") as GenerationSection;
        const data = currentGenerated.get(sectionId);
        if (data) {
          const label = sectionId.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
          result.push({ title: label, content: data });
        }
      }
    }
    return result;
  };

  const getSectionDisplayName = (section: GenerationSection): string => {
    return section.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getSectionsForStep = (step: WizardStep): GenerationSection[] => {
    switch (step) {
      case WizardStep.PATIENT_DATA:
        return [
          GenerationSection.DOCUMENT_HEADER, GenerationSection.PATIENT_INFORMATION,
          GenerationSection.DIAGNOSTIC_INFORMATION, GenerationSection.HISTORY_OF_SERVICES,
          GenerationSection.RELEVANT_HISTORY, GenerationSection.DIRECT_AND_INDIRECT_MEASURES,
        ];
      case WizardStep.ASSESSMENT_RESULTS:
        return [
          GenerationSection.INITIAL_OBSERVATION, GenerationSection.LANGUAGE_DOMAIN,
          GenerationSection.SOCIAL_AND_PLAY_DOMAIN, GenerationSection.ATTENDING_SKILLS_DOMAIN,
          GenerationSection.DAILY_LIVING_DOMAIN, GenerationSection.SAFETY_SKILLS_DOMAIN,
          GenerationSection.FUNCTIONAL_BEHAVIOR_ASSESSMENT,
        ];
      case WizardStep.TREATMENT_GOALS:
        return [
          GenerationSection.CLINICAL_RECOMMENDATIONS, GenerationSection.LANGUAGE_GOALS,
          GenerationSection.SOCIAL_AND_PLAY_GOALS, GenerationSection.ATTENDING_GOALS,
          GenerationSection.DAILY_LIVING_GOALS, GenerationSection.SAFETY_GOALS,
          GenerationSection.CAREGIVER_GOALS,
        ];
      case WizardStep.SUPPORT_PLAN:
        return [
          GenerationSection.PLAN_FOR_GENERALIZATION, GenerationSection.TRANSITION_PLANNING,
          GenerationSection.CRITERIA_FOR_DISCHARGE, GenerationSection.COORDINATION_OF_CARE,
          GenerationSection.CRISIS_MANAGEMENT_PLANNING, GenerationSection.REQUESTED_HOURS_OF_SERVICE,
        ];
      default:
        return [];
    }
  };

  const handleGenerateAll = async () => {
    const stepSections = getSectionsForStep(currentStep);
    // Skip sections that already have generated content
    const sectionsToGenerate = stepSections.filter(s => {
      const state = sectionStates.get(s);
      return !state?.generatedContent;
    });

    if (sectionsToGenerate.length === 0) return;

    const initialItems = sectionsToGenerate.map(section => ({
      id: section,
      name: getSectionDisplayName(section),
      status: "pending" as SectionGenStatus,
    }));

    setIsGeneratingAll(true);
    setGenerationProgress({ total: sectionsToGenerate.length, sections: initialItems });

    const updateStatus = (section: GenerationSection, status: SectionGenStatus) => {
      setGenerationProgress(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map(s => s.id === section ? { ...s, status } : s),
        };
      });
    };

    // Build a resolved config map: use existing state if present, otherwise fetch from backend
    const resolvedConfigs = new Map<GenerationSection, {
      model: string; refs: string[]; sys: string; user: string;
      ragEnabled: boolean; ragNumChunks: number; ragMinScore: number;
    }>();

    await Promise.allSettled(
      sectionsToGenerate.map(async (section) => {
        const existing = sectionStates.get(section);
        if (existing?.selectedModel && existing?.selectedReferences?.length) {
          resolvedConfigs.set(section, {
            model: existing.selectedModel,
            refs: existing.selectedReferences,
            sys: existing.systemPrompt,
            user: existing.userPrompt,
            ragEnabled: existing.ragEnabled || false,
            ragNumChunks: existing.ragNumChunks || 5,
            ragMinScore: existing.ragMinScore || 0.3,
          });
          return;
        }
        try {
          const config = await getSectionConfig(section);
          const model = config.default_model || (models.length > 0 ? models[0].id : "");
          const refs: string[] = config.default_references || [];
          resolvedConfigs.set(section, {
            model,
            refs,
            sys: config.system_prompt || "",
            user: config.user_prompt || "",
            ragEnabled: true,
            ragNumChunks: 5,
            ragMinScore: 0.3,
          });
          // Also persist to section state so the UI shows the loaded config
          updateSectionState(section, {
            selectedModel: model,
            selectedReferences: refs,
            systemPrompt: config.system_prompt || "",
            userPrompt: config.user_prompt || "",
            ragEnabled: true,
            ragNumChunks: 5,
            ragMinScore: 0.3,
          });
        } catch (error) {
          console.error(`Failed to load config for ${section}:`, error);
        }
      })
    );

    const generateSingle = async (section: GenerationSection): Promise<void> => {
      const cfg = resolvedConfigs.get(section);
      if (!cfg || !cfg.model || !cfg.refs.length) {
        updateStatus(section, "skipped");
        return;
      }

      const referenceData = buildReferenceData(cfg.refs, analysisResultsMap, generatedSections);

      if (referenceData.length === 0) {
        updateStatus(section, "skipped");
        return;
      }

      updateStatus(section, "generating");
      try {
        const result = await generateTreatmentPlanSection(
          section,
          cfg.model,
          cfg.sys,
          cfg.user,
          referenceData,
          cfg.ragEnabled,
          cfg.ragNumChunks,
          cfg.ragMinScore,
          activeSessionId,
        );

        const ragChunks = result.metadata?.rag_metadata?.chunks || result.metadata?.rag_chunks;

        updateSectionState(section, {
          generatedContent: result.data || result,
          lastGeneratedAt: new Date().toISOString(),
          lastGeneratedModel: cfg.model,
          isGenerating: false,
          hasUnsavedChanges: false,
          ragChunks: ragChunks || [],
        });

        updateStatus(section, "done");
      } catch (error) {
        console.error(`Error generating ${section}:`, error);
        updateStatus(section, "error");
      }
    };

    try {
      await Promise.allSettled(sectionsToGenerate.map(section => generateSingle(section)));
    } finally {
      setIsGeneratingAll(false);
      setGenerationProgress(null);
    }
  };

  const handleNextStep = async () => {
    if (currentStep >= WizardStep.REVIEW) return;
    handleStepChange(currentStep + 1 as WizardStep);
  };

  const handlePrevStep = () => {
    if (currentStep > WizardStep.UPLOAD_DOCUMENTS) {
      handleStepChange(currentStep - 1 as WizardStep);
    }
  };

  const handleQuit = () => {
    setActiveSessionId(null);
    loadSessions();
  };

  // File upload handlers (Step 1)
  const handleInputStateChange = (documentType: DocumentType, newState: InputPanelState) => {
    setInputStateMap(prev => ({
      ...prev,
      [documentType]: newState,
    }));
  };

  const handleFileUpload = (documentType: DocumentType, file: File) => {
    setInputStateMap(prev => ({
      ...prev,
      [documentType]: {
        ...prev[documentType],
        file: file,
      },
    }));
    // If user re-adds a file for a type that was pending clear, remove from pending clears
    setPendingClears(prev => {
      if (!prev.has(documentType)) return prev;
      const next = new Set(prev);
      next.delete(documentType);
      return next;
    });
  };

  const handleFileClear = (documentType: DocumentType) => {
    setInputStateMap(prev => ({
      ...prev,
      [documentType]: {
        ...prev[documentType],
        file: null,
      },
    }));
    // Only mark as pending clear if the doc exists on the server
    const hasServerFile = !!uploadedFiles[documentType];
    const hasServerAnalysis = analysisResultsMap[documentType] !== null && analysisResultsMap[documentType] !== undefined;
    if (hasServerFile || hasServerAnalysis) {
      setPendingClears(prev => new Set(prev).add(documentType));
    }
  };

  // Section generation handlers (Steps 2-5)
  const getSectionState = (section: GenerationSection): SectionGenerationState => {
    return sectionStates.get(section) || createInitialSectionState();
  };

  const updateSectionState = (section: GenerationSection, updates: Partial<SectionGenerationState>) => {
    setSectionStates(prev => {
      const newMap = new Map(prev);
      const currentState = newMap.get(section) || createInitialSectionState();
      newMap.set(section, { ...currentState, ...updates });
      return newMap;
    });
  };

  const handleSectionDataChange = (section: GenerationSection, newData: any) => {
    setSectionStates(prev => {
      const newMap = new Map(prev);
      const currentState = newMap.get(section) || createInitialSectionState();
      newMap.set(section, {
        ...currentState,
        generatedContent: newData,
        hasUnsavedChanges: true,
      });
      return newMap;
    });
  };

  const handleGenerateSection = async (data: {
    section: GenerationSection;
    modelId: string;
    systemPrompt: string;
    userPrompt: string;
    referenceData: Array<{ title: string; content: any }>;
    ragEnabled: boolean;
    ragNumChunks: number;
    ragMinScore: number;
  }) => {
    const sectionName = getSectionDisplayName(data.section);

    setIsGeneratingAll(true);
    setGenerationProgress({
      total: 1,
      sections: [{ id: data.section, name: sectionName, status: "generating" }],
    });

    updateSectionState(data.section, {
      isGenerating: true,
      hasUnsavedChanges: false,
    });

    try {
      const result = await generateTreatmentPlanSection(
        data.section,
        data.modelId,
        data.systemPrompt,
        data.userPrompt,
        data.referenceData,
        data.ragEnabled,
        data.ragNumChunks,
        data.ragMinScore,
        activeSessionId
      );

      const ragChunks = result.metadata?.rag_metadata?.chunks || result.metadata?.rag_chunks;

      updateSectionState(data.section, {
        generatedContent: result.data || result,
        lastGeneratedAt: new Date().toISOString(),
        lastGeneratedModel: data.modelId,
        isGenerating: false,
        hasUnsavedChanges: false,
        ragChunks: ragChunks || [],
      });

      setGenerationProgress({
        total: 1,
        sections: [{ id: data.section, name: sectionName, status: "done" }],
      });
    } catch (error) {
      console.error("Error generating section:", error);
      updateSectionState(data.section, { isGenerating: false });
      setGenerationProgress({
        total: 1,
        sections: [{ id: data.section, name: sectionName, status: "error" }],
      });
    } finally {
      setIsGeneratingAll(false);
      setGenerationProgress(null);
    }
  };

  const getDefaultSectionForStep = (step: WizardStep): GenerationSection => {
    switch (step) {
      case WizardStep.PATIENT_DATA:
        return GenerationSection.PATIENT_INFORMATION;
      case WizardStep.ASSESSMENT_RESULTS:
        return GenerationSection.INITIAL_OBSERVATION;
      case WizardStep.TREATMENT_GOALS:
        return GenerationSection.CLINICAL_RECOMMENDATIONS;
      case WizardStep.SUPPORT_PLAN:
        return GenerationSection.PLAN_FOR_GENERALIZATION;
      default:
        return GenerationSection.PATIENT_INFORMATION;
    }
  };

  // Set active section when step changes
  useEffect(() => {
    if (currentStep >= WizardStep.PATIENT_DATA && currentStep <= WizardStep.SUPPORT_PLAN) {
      setActiveSection(getDefaultSectionForStep(currentStep));
    }
  }, [currentStep]);

  // Discard local file changes when leaving step 1 (e.g. exit, step indicator click)
  // "Next" already synced to server before changing step, so this is a safe reset.
  useEffect(() => {
    if (currentStep !== WizardStep.UPLOAD_DOCUMENTS) {
      setPendingClears(new Set());
      setInputStateMap(prev => {
        const reset = {} as Record<DocumentType, InputPanelState>;
        for (const key of Object.keys(prev) as DocumentType[]) {
          reset[key] = { ...prev[key], file: null };
        }
        return reset;
      });
    }
  }, [currentStep]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case WizardStep.UPLOAD_DOCUMENTS:
        return (
          <UploadDocumentsStep
            sessionId={activeSessionId}
            activeDocumentType={activeDocumentType}
            uploadedFiles={uploadedFiles}
            inputStateMap={inputStateMap}
            pendingClears={pendingClears}
            onInputStateChange={handleInputStateChange}
            onFileUpload={handleFileUpload}
            onFileClear={handleFileClear}
          />
        );

      case WizardStep.PATIENT_DATA:
      case WizardStep.ASSESSMENT_RESULTS:
      case WizardStep.TREATMENT_GOALS:
      case WizardStep.SUPPORT_PLAN:
        return (
          <SectionGenerationStep
            models={models}
            activeSection={activeSection}
            sectionStates={sectionStates}
            onSectionStateChange={updateSectionState}
            onGenerate={handleGenerateSection}
            onSectionDataChange={handleSectionDataChange}
            analysisResults={analysisResultsMap}
            generatedSections={generatedSections}
            inputStateMap={inputStateMap}
            uploadedFiles={uploadedFiles}
            sessionId={activeSessionId}
            isGenerating={getSectionState(activeSection).isGenerating}
          />
        );

      case WizardStep.REVIEW:
        return (
          <ReviewStep
            ref={reviewStepRef}
            generatedSections={generatedSections}
            onSectionDataChange={handleSectionDataChange}
          />
        );

      default:
        return null;
    }
  };

  // If no active session, show the client selector
  if (!activeSession) {
    return (
      <div className="treatment-plan-wizard">
        <div className="wizard-main wizard-main--expanded">
          <div className="wizard-content">
            {isLoadingSessions ? (
              <div className="wizard-loading">
                <div className="spinner"></div>
                <p>Loading sessions...</p>
              </div>
            ) : (
              <ClientSelector
                sessions={sessions}
                isLoading={isLoadingSessions}
                onCreateSession={handleCreateSession}
                onResumeSession={handleResumeSession}
                onDeleteSession={handleDeleteSession}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="treatment-plan-wizard">
      {/* Header with Client Info + Step Indicator */}
      <WizardStepIndicator
        currentStep={currentStep}
        onStepClick={handleStepChange}
        clientName={activeSession.client_name}
        clientDob={activeSession.date_of_birth}
        onExit={handleQuit}
      />

      {/* Analysis Progress Overlay */}
      {isAnalyzing && analysisProgress && (
        <div className="analysis-overlay">
          <div className="analysis-overlay-content">
            <div className="spinner"></div>
            <h3>Analyzing Documents</h3>
            <p className="analysis-summary">
              {analysisProgress.docs.filter(d => d.status === "done").length} of{" "}
              {analysisProgress.total} complete
            </p>
            <div className="analysis-progress-bar">
              <div
                className="analysis-progress-fill"
                style={{
                  width: `${(analysisProgress.docs.filter(d => d.status === "done" || d.status === "error").length / analysisProgress.total) * 100}%`,
                }}
              />
            </div>
            <ul className="analysis-doc-list">
              {analysisProgress.docs.map(doc => (
                <li key={doc.type} className={`analysis-doc-item analysis-doc-${doc.status}`}>
                  <span className="analysis-doc-icon">
                    {doc.status === "pending" && "\u23F3"}
                    {doc.status === "analyzing" && "\u2699\uFE0F"}
                    {doc.status === "done" && "\u2705"}
                    {doc.status === "error" && "\u274C"}
                  </span>
                  <span className="analysis-doc-name">{doc.name}</span>
                  <span className="analysis-doc-status">
                    {doc.status === "pending" && "Waiting"}
                    {doc.status === "analyzing" && "Analyzing..."}
                    {doc.status === "done" && "Done"}
                    {doc.status === "error" && "Failed"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Generation Progress Overlay */}
      {isGeneratingAll && generationProgress && (
        <div className="analysis-overlay">
          <div className="analysis-overlay-content">
            <div className="spinner"></div>
            <h3>Generating Sections</h3>
            <p className="analysis-summary">
              {generationProgress.sections.filter(s => s.status === "done").length} of{" "}
              {generationProgress.sections.filter(s => s.status !== "skipped").length} complete
            </p>
            <div className="analysis-progress-bar">
              <div
                className="analysis-progress-fill"
                style={{
                  width: `${(generationProgress.sections.filter(s => s.status === "done" || s.status === "error" || s.status === "skipped").length / generationProgress.total) * 100}%`,
                }}
              />
            </div>
            <ul className="analysis-doc-list">
              {generationProgress.sections.map(sec => (
                <li key={sec.id} className={`analysis-doc-item analysis-doc-${sec.status}`}>
                  <span className="analysis-doc-icon">
                    {sec.status === "pending" && "\u23F3"}
                    {sec.status === "generating" && "\u2699\uFE0F"}
                    {sec.status === "done" && "\u2705"}
                    {sec.status === "skipped" && "\u23ED\uFE0F"}
                    {sec.status === "error" && "\u274C"}
                  </span>
                  <span className="analysis-doc-name">{sec.name}</span>
                  <span className="analysis-doc-status">
                    {sec.status === "pending" && "Waiting"}
                    {sec.status === "generating" && "Generating..."}
                    {sec.status === "done" && "Done"}
                    {sec.status === "skipped" && "Skipped"}
                    {sec.status === "error" && "Failed"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="wizard-body">
        {/* Left Sidebar */}
        <TreatmentPlanSidebar
          currentStep={currentStep}
          activeDocumentType={activeDocumentType}
          onDocumentTypeChange={setActiveDocumentType}
          inputStateMap={inputStateMap}
          analysisResultsMap={analysisResultsMap}
          analyzedFileKeys={analyzedFileKeys}
          uploadedFiles={uploadedFiles}
          pendingClears={pendingClears}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          generatedSections={generatedSections}
          onReviewSectionClick={(section) => reviewStepRef.current?.scrollToSection(section)}
        />

        {/* Main Content */}
        <div className="wizard-main">
          <div className="wizard-content">
            {isLoadingSession ? (
              <div className="wizard-loading">
                <div className="spinner"></div>
                <p>Loading session...</p>
              </div>
            ) : (
              renderStepContent()
            )}
          </div>

          {/* Footer Navigation */}
          <div className="wizard-navigation">
            <button
              className="btn btn-secondary"
              onClick={handlePrevStep}
              disabled={currentStep === WizardStep.UPLOAD_DOCUMENTS || isAnalyzing}
            >
              Previous
            </button>

            {currentStep === WizardStep.UPLOAD_DOCUMENTS && (
              <button
                className="btn btn-primary btn-center-action"
                onClick={handleApplyChanges}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? "Analyzing..." : "Apply Changes"}
              </button>
            )}

            {currentStep >= WizardStep.PATIENT_DATA && currentStep <= WizardStep.SUPPORT_PLAN && (
              <button
                className="btn btn-primary btn-center-action"
                onClick={handleGenerateAll}
                disabled={isGeneratingAll}
              >
                {isGeneratingAll ? "Generating..." : "Generate All"}
              </button>
            )}

            <button
              className="btn btn-secondary"
              onClick={handleNextStep}
              disabled={currentStep === WizardStep.REVIEW || isAnalyzing}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanWizard;
